import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { createAppointment } from "../../lib/api";
import { SITE } from "../../lib/site";
import Reveal from "../Reveal";

const services = ["Banho", "Tosa", "Banho e Tosa", "Tosa Higiênica", "Higienização", "Outro"];

export default function Appointment() {
  const [form, setForm] = useState({ name: "", phone: "", pet_name: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.phone || !form.pet_name || !form.service) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    setLoading(true);

    try {
      await createAppointment(form);
      setSuccess(true);
      toast.success("Agendamento recebido! Redirecionando para o WhatsApp…");

      const text =
        `Olá Tia Sandra! 🐾%0A%0A` +
        `Acabei de solicitar um agendamento pelo site:%0A` +
        `*Nome:* ${form.name}%0A` +
        `*Telefone:* ${form.phone}%0A` +
        `*Pet:* ${form.pet_name}%0A` +
        `*Serviço:* ${form.service}%0A` +
        (form.message ? `*Mensagem:* ${form.message}%0A` : "");

      setTimeout(() => {
        window.open(`https://wa.me/${SITE.whatsappNumber}?text=${text}`, "_blank");
      }, 700);

      setForm({ name: "", phone: "", pet_name: "", service: "", message: "" });
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="agendamento"
      data-testid="appointment-section"
      className="relative overflow-hidden py-24 sm:py-32 text-white bg-transparent"
    >
      <div className="absolute inset-0 paw-tile opacity-10 pointer-events-none" />

      <div className="container-pad relative z-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <Reveal as="div" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-xl border border-white/15 text-white text-xs font-bold uppercase tracking-[0.18em] mb-5">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            Agende em 30s
          </Reveal>

          <Reveal as="h2" delay={80} className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.1]">
            Reserve um <span className="italic text-gold">horário</span> para o seu pet.
          </Reveal>

          <Reveal as="p" delay={140} className="mt-5 text-base sm:text-lg text-white/75 leading-relaxed">
            Preencha o formulário e nós retornamos pelo WhatsApp para confirmar dia, hora e detalhes do atendimento.
          </Reveal>

          <Reveal as="div" delay={200} className="mt-8 grid grid-cols-2 gap-4">
            <div className="p-4 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
              <div className="text-2xl font-heading text-white font-semibold">+500</div>
              <div className="text-xs text-white/60">Famílias atendidas</div>
            </div>

            <div className="p-4 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
              <div className="text-2xl font-heading text-white font-semibold">4,9★</div>
              <div className="text-xs text-white/60">Avaliação média</div>
            </div>
          </Reveal>
        </div>

        <Reveal as="div" delay={120} className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="appointment-form"
            className="bg-white/10 backdrop-blur-2xl rounded-[32px] p-7 sm:p-10 shadow-[0_35px_100px_rgba(0,0,0,0.30)] border border-white/15"
          >
            {success && (
              <div className="mb-6 flex items-center gap-3 bg-emerald-400/15 text-emerald-100 border border-emerald-300/25 rounded-2xl p-4">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <p className="text-sm font-medium">Agendamento recebido! Te encaminhamos para o WhatsApp.</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-white font-semibold text-sm">Nome *</Label>
                <Input
                  id="name"
                  data-testid="appt-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Seu nome"
                  className="mt-2 h-12 rounded-xl bg-white/90 border-white/20 text-royal placeholder:text-slate-400 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-white font-semibold text-sm">Telefone *</Label>
                <Input
                  id="phone"
                  data-testid="appt-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="(47) 99999-9999"
                  className="mt-2 h-12 rounded-xl bg-white/90 border-white/20 text-royal placeholder:text-slate-400 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>

              <div>
                <Label htmlFor="pet" className="text-white font-semibold text-sm">Nome do Pet *</Label>
                <Input
                  id="pet"
                  data-testid="appt-pet"
                  value={form.pet_name}
                  onChange={(e) => update("pet_name", e.target.value)}
                  placeholder="Ex: Thor, Luna…"
                  className="mt-2 h-12 rounded-xl bg-white/90 border-white/20 text-royal placeholder:text-slate-400 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>

              <div>
                <Label htmlFor="service" className="text-white font-semibold text-sm">Serviço *</Label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    id="service"
                    data-testid="appt-service"
                    className="mt-2 h-12 rounded-xl bg-white/90 border-white/20 text-royal focus:ring-gold"
                  >
                    <SelectValue placeholder="Escolha um serviço" />
                  </SelectTrigger>

                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="message" className="text-white font-semibold text-sm">Mensagem</Label>
                <Textarea
                  id="message"
                  data-testid="appt-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Conte um pouco sobre o seu pet, preferências, restrições…"
                  rows={4}
                  className="mt-2 rounded-xl bg-white/90 border-white/20 text-royal placeholder:text-slate-400 focus-visible:ring-gold focus-visible:border-gold"
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              <p className="text-xs text-white/55">Ao enviar você concorda em ser contactado(a) via WhatsApp.</p>

              <Button
                type="submit"
                data-testid="appt-submit"
                disabled={loading}
                className="rounded-full bg-gold hover:bg-gold-hover text-royal font-bold px-8 py-6 shadow-[0_15px_40px_rgba(242,169,0,.35)] hover:shadow-[0_20px_55px_rgba(242,169,0,.5)] hover:-translate-y-0.5 transition-all disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando…
                  </>
                ) : (
                  "Agendar Atendimento"
                )}
              </Button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
